import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { IconButton } from '../Button';
import Button from '../Button';
import Share from '../Share';
import { logGameEvent, trackUiElementClicked } from '../../analytics';
import { usePuzzleInstances } from '../../contexts/PuzzleInstancesContext';
import { useGlobalModal } from '../../contexts/ModalContext';
import { createOrGetSession } from '../../api/gameplay';
import { addUtmSource } from '../../utils/utm';
import styles from '../../styles/layout/GameHeader.module.css';
import modalStyles from '../../styles/modal.module.css';

const getActionBaseKey = (action, index) => {
  return action.key
    || [action.icon, action.title, action.className]
      .filter(Boolean)
      .join('|')
    || `action-${index}`;
};

const getActionAnalyticsId = (action, index) => {
  return action.analyticsId || action.key || action.title || action.icon || `action-${index}`;
};

/**
 * GameHeader - Displays game title with icon and action buttons
 * 
 * @param {string} title - Game title (e.g., "Collegamenti")
 * @param {React.Component|function} icon - Game icon component
 * @param {Object} gameConfig - Game configuration from games.js
 * @param {Array} actions - Array of action button configs
 *   Each action: { key?: string, icon: string, title: string, onClick: function, disabled?: boolean }
 * @param {Object} snackAlertRef - Optional ref to show share success message
 */
const GameHeader = ({ 
  title, 
  icon: IconComponent, 
  gameConfig,
  actions = [],
  showShareAction = true,
  snackAlertRef,
  sponsorship = null,
  previewMode = false,
  isLoading = false,
}) => {
  const { date } = useParams();
  const { getPuzzleInstance, fetchPuzzleInstance, isLoading: isPuzzleInstancesLoading, cacheVersion } = usePuzzleInstances();
  const { openModal, closeModal } = useGlobalModal();
  const { isSignedIn, getToken } = useAuth();

  // Try to fetch puzzle instance if not in cache (in case it's not loaded yet)
  useEffect(() => {
    if (!isLoading && date && gameConfig && !isPuzzleInstancesLoading) {
      const instance = getPuzzleInstance(gameConfig.id, date);
      if (!instance) {
        // Try to fetch it
        fetchPuzzleInstance(gameConfig.id, date).catch(err => {
          // Silently fail - this is okay, useGameplaySession will handle it
          console.debug('Could not fetch puzzle instance for replay check:', err);
        });
      }
    }
  }, [date, gameConfig, getPuzzleInstance, fetchPuzzleInstance, isPuzzleInstancesLoading, isLoading]);

  // Check if there's an updated puzzle version available
  const puzzleUpdateInfo = useMemo(() => {
    if (isLoading || !date || !gameConfig || isPuzzleInstancesLoading) {
      return null;
    }

    const instance = getPuzzleInstance(gameConfig.id, date);
    
    // If not in cache, we can't check yet (will be fetched by useGameplaySession)
    if (!instance) {
      return null;
    }

    // Check if user has played and there's a new version
    // Condition: user_session exists and user_session.puzzle_instance_id !== puzzle_instance_id
    if (instance.user_session && 
        instance.user_session.puzzle_instance_id !== instance.puzzle_instance_id) {
      return {
        hasUpdate: true,
        newPuzzleInstanceId: instance.puzzle_instance_id,
        oldPuzzleInstanceId: instance.user_session.puzzle_instance_id,
      };
    }

    return null;
  }, [date, gameConfig, getPuzzleInstance, isPuzzleInstancesLoading, cacheVersion, isLoading]);

  if (!title) return null;

  const handleShareClick = () => {
    trackUiElementClicked({
      source_surface: 'game_header',
      surface: 'game_header_actions',
      game_slug: gameConfig?.id || null,
      element_id: 'share',
      element_title: 'Condividi',
      action: 'click',
    });

    if (date) {
      logGameEvent('share_clicked', gameConfig.id, date);
    }
  };

  const handleShareSuccess = () => {
    if (date) {
      logGameEvent('share_success', gameConfig.id, date);
    }
    snackAlertRef?.current?.showSnackAlert("Condivisione riuscita!", "success", 2000);
  };

  const handleShareError = (error) => {
    if (date) {
      logGameEvent('share_error', gameConfig.id, date, { error: error.message });
    }
  };

  const handleReplayClick = () => {
    if (!puzzleUpdateInfo) return;

    const ReplayModalContent = () => {
      // Move loading state inside the modal content component so it re-renders properly
      const [isReplaying, setIsReplaying] = useState(false);

      const handleConfirm = async () => {
        setIsReplaying(true);

        try {
          const token = isSignedIn 
            ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
            : null;

          // Create a new session with the new puzzle instance ID
          const result = await createOrGetSession(
            puzzleUpdateInfo.newPuzzleInstanceId,
            {},
            token
          );

          if (result.success) {
            // Close modal before reload (reload will happen anyway, but this is cleaner)
            closeModal();
            // Reload the page to start with the new session
            window.location.reload();
          } else {
            snackAlertRef?.current?.showSnackAlert(
              "Errore durante il riavvio. Riprova.",
              "error",
              3000
            );
            setIsReplaying(false);
            // Keep modal open so user can try again or cancel
          }
        } catch (error) {
          console.error('Error creating new session:', error);
          snackAlertRef?.current?.showSnackAlert(
            "Errore durante il riavvio. Riprova.",
            "error",
            3000
          );
          setIsReplaying(false);
          // Keep modal open so user can try again or cancel
        }
      };

      return (
        <div className={modalStyles.modalContent}>
          <p>
            C'è una nuova versione di questa partita! Vuoi provarla?
          </p>
          <p>
            Il tuo progresso su questa versione verrà archiviato e potrai iniziare il nuovo puzzle da zero.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <Button
              onClick={closeModal}
              disabled={isReplaying}
            >
              Annulla
            </Button>
            <Button
              primary
              onClick={handleConfirm}
              disabled={isReplaying}
              loading={isReplaying}
              loadingText="Conferma"
            >
              Conferma
            </Button>
          </div>
        </div>
      );
    };

    openModal({
      alignToGame: true,
      title: 'Nuova versione disponibile',
      content: <ReplayModalContent />,
      showHeader: true,
      analytics: {
        source_surface: 'game_header',
        modal_name: 'replay_confirmation',
        game_slug: gameConfig?.id || null,
        trigger: 'replay_action_click',
      },
    });
  };

  const handleSponsorClick = () => {
    if (previewMode) {
      return;
    }
    const sponsorUrlWithUtm = addUtmSource(sponsorship?.sponsor_url);
    if (!date || !gameConfig?.id || !sponsorUrlWithUtm) {
      return;
    }

    logGameEvent('sponsor_click', gameConfig.id, date, {
      sponsor_url: sponsorUrlWithUtm,
      sponsor_title: sponsorship.sponsor_title || null,
    });
  };

  // Build actions list
  // 1. Replay button (if puzzle update available) - first and in red
  // 2. Other actions
  const allActions = [];
  
  // Add replay button first if there's an update
  if (puzzleUpdateInfo?.hasUpdate) {
    allActions.push({
      key: 'replay',
      icon: 'fa fa-redo',
      title: 'Nuova versione disponibile',
      onClick: handleReplayClick,
      className: styles.replayButton // Add red styling
    });
  }
  
  // Add other actions after replay button
  allActions.push(...actions);

  // Build stable, unique keys for React reconciliation.
  const seenActionKeys = new Map();
  const keyedActions = allActions.map((action, index) => {
    const baseKey = getActionBaseKey(action, index);
    const seenCount = seenActionKeys.get(baseKey) || 0;
    seenActionKeys.set(baseKey, seenCount + 1);

    return {
      action,
      actionKey: seenCount === 0 ? baseKey : `${baseKey}-${seenCount}`,
      actionAnalyticsId: getActionAnalyticsId(action, index),
    };
  });

  const handleActionClick = (action, actionAnalyticsId) => {
    trackUiElementClicked({
      source_surface: 'game_header',
      surface: 'game_header_actions',
      game_slug: gameConfig?.id || null,
      element_id: actionAnalyticsId,
      element_title: action.title || null,
      action: 'click',
    });
    action.onClick?.();
  };

  const hasSponsorLine = !!(sponsorship?.sponsor_title && sponsorship?.sponsor_url);
  const sponsorHeaderUrl = addUtmSource(sponsorship?.sponsor_url);
  const shareInsertIndex = puzzleUpdateInfo?.hasUpdate ? 1 : 0;
  const shouldRenderShare = showShareAction && !!gameConfig;
  const shareTitle = "Prova un giochino di parole!";
  const shareText = `Ho giocato a ${gameConfig?.displayName}! 🧩 Prova anche tu!`;
  const shareUrl = `${window.location.origin}/${gameConfig?.pathName}${date ? `/${date}` : ''}`;
  const shouldRenderAnyAction = keyedActions.length > 0 || shouldRenderShare;

  return (
    <header className={`${styles.header} ${hasSponsorLine ? styles.headerWithSponsor : ''}`}>
      <div className={styles.headerContent}>
        {/* Game icon and title */}
        <div className={styles.titleSection}>
          {IconComponent && (
            <span className={styles.icon}>
              {typeof IconComponent === 'function' 
                ? <IconComponent size={28} renderMode="solid" /> 
                : IconComponent}
            </span>
          )}
          <div className={styles.titleCopy}>
            <h1 className={styles.title}>{title}</h1>
            {hasSponsorLine && (
              <div className={styles.sponsorLine}>
                sponsorizzato da{' '}
                <a
                  href={sponsorHeaderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.sponsorLink}
                  data-google-interstitial="false"
                  data-google-vignette="false"
                  onClick={(event) => {
                    if (previewMode && sponsorship?.is_placeholder_sponsor_url) {
                      event.preventDefault();
                      return;
                    }
                    handleSponsorClick();
                  }}
                >
                  {sponsorship?.is_placeholder_title ? (
                    <em>{sponsorship.sponsor_title}</em>
                  ) : (
                    sponsorship.sponsor_title
                  )}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        {shouldRenderAnyAction && (
          <div className={styles.actions}>
            {keyedActions.map(({ action, actionKey, actionAnalyticsId }, index) => (
              <React.Fragment key={actionKey}>
                {shouldRenderShare && index === shareInsertIndex && (
                  <Share
                    alignToGame
                    variant="icon"
                    className={styles.actionButton}
                    title={shareTitle}
                    text={shareText}
                    url={shareUrl}
                    iconTitle="Condividi"
                    onShareClick={handleShareClick}
                    onShareSuccess={handleShareSuccess}
                    onShareError={handleShareError}
                  />
                )}
                <IconButton
                  iconClassName={action.icon}
                  onClick={() => handleActionClick(action, actionAnalyticsId)}
                  className={`${styles.actionButton} ${action.className || ''}`}
                  title={action.title}
                  disabled={action.disabled}
                />
              </React.Fragment>
            ))}
            {shouldRenderShare && keyedActions.length === shareInsertIndex && (
              <Share
                alignToGame
                variant="icon"
                className={styles.actionButton}
                title={shareTitle}
                text={shareText}
                url={shareUrl}
                iconTitle="Condividi"
                onShareClick={handleShareClick}
                onShareSuccess={handleShareSuccess}
                onShareError={handleShareError}
              />
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default GameHeader;
